import { Reveal, Label } from "./Reveal";
import { motion } from "framer-motion";

const services = [
  {
    n: "01",
    title: "House Painting",
    body: "Fresh interior and exterior repaints that transform a home. Clean prep, tidy lines and durable finishes — like these crisp weatherboard exteriors we recently completed.",
    img: "https://customer-assets-gfyr7b9c.emergentagent.net/job_berm-maintenance/artifacts/dtjrofje_IMG_8883.webp",
    imgs: [
      "https://customer-assets-gfyr7b9c.emergentagent.net/job_berm-maintenance/artifacts/dtjrofje_IMG_8883.webp",
      "https://customer-assets-gfyr7b9c.emergentagent.net/job_berm-maintenance/artifacts/mh9rhdru_2DC67BAE-7861-46C8-87CB-9ABD84A51875.webp",
    ],
    tags: ["Exterior", "Repaint"],
  },
  {
    n: "02",
    title: "Bathroom & Home Renovations",
    body: "Full bathroom and home renovations — fresh tiling, vanities, showers and floors finished to a high standard. Quality work that lifts the whole home, like this crisp marble-tiled bathroom.",
    imgs: [
      "https://customer-assets-gfyr7b9c.emergentagent.net/job_berm-maintenance/artifacts/4f7eqvck_IMG_4905.webp",
      "https://customer-assets-gfyr7b9c.emergentagent.net/job_berm-maintenance/artifacts/6rqmqbxb_IMG_4909.webp",
    ],
    img: "https://customer-assets-gfyr7b9c.emergentagent.net/job_berm-maintenance/artifacts/4f7eqvck_IMG_4905.webp",
    tags: ["Bathrooms", "Interior"],
  },
  {
    n: "03",
    title: "Fencing",
    body: "Broken, leaning or storm-damaged fences repaired and rebuilt fast. Quick to quote, quicker to finish — with communication the whole way through.",
    img: "https://customer-assets-gfyr7b9c.emergentagent.net/job_berm-maintenance/artifacts/e5jpbqqz_IMG_9380.webp",
    tags: ["Timber", "Boundaries"],
  },
  {
    n: "04",
    title: "Lawn Mowing & Grounds",
    body: "Regular mowing you never have to think about. We keep an eye on the weather and keep you updated — you just come home to a tidy lawn.",
    img: "https://images.unsplash.com/photo-1628340981113-fe1949fe5cc0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
    tags: ["Recurring", "Grounds"],
  },
  {
    n: "05",
    title: "Rubbish Removal",
    body: "Green-waste, clean-outs and full site clears hauled away fast — you don't lift a thing. Watch a full load disappear in this on-site timelapse.",
    video: "/videos/rubbish-removal.mp4",
    poster: "/videos/rubbish-removal-poster.jpg",
    tags: ["Clearance", "Green-waste"],
  },
];

const Row = ({ s, i }) => {
  const flip = i % 2 === 1;
  return (
    <div className="relative border-t border-forest-ink/15 py-12 md:py-20">
      <span className="pointer-events-none absolute -top-6 md:-top-10 right-0 font-serif text-[22vw] md:text-[12vw] leading-none text-forest-ink/[0.05] select-none">
        {s.n}
      </span>
      <div
        className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center relative ${
          flip ? "lg:[direction:rtl]" : ""
        }`}
      >
        <div className="lg:col-span-6 [direction:ltr]">
          <Reveal>
            {s.video ? (
              <div className="mx-auto max-w-[360px] overflow-hidden rounded-sm border border-forest-ink/10 bg-forest-ink shadow-xl">
                <video
                  src={s.video}
                  poster={s.poster}
                  autoPlay
                  muted
                  loop
                  playsInline
                  data-testid="rubbish-removal-video"
                  className="aspect-[3/4] w-full object-cover"
                />
              </div>
            ) : s.imgs ? (
              <div className="grid grid-cols-2 gap-3">
                {s.imgs.map((src, idx) => (
                  <div
                    key={idx}
                    className="overflow-hidden rounded-sm border border-forest-ink/10 group"
                  >
                    <motion.img
                      src={src}
                      alt={`${s.title} ${idx + 1}`}
                      whileHover={{ scale: 1.06 }}
                      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                      className="aspect-[3/4] w-full object-cover"
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="overflow-hidden rounded-sm border border-forest-ink/10 group">
                <motion.img
                  src={s.img}
                  alt={s.title}
                  whileHover={{ scale: 1.06 }}
                  transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                  className="aspect-[16/11] w-full object-cover"
                />
              </div>
            )}
          </Reveal>
        </div>
        <div className="lg:col-span-6 [direction:ltr]">
          <Reveal delay={0.1}>
            <Label>Service — {s.n}</Label>
            <h3 className="mt-4 font-serif text-4xl md:text-5xl tracking-tight text-forest-ink leading-[0.95]">
              {s.title}
            </h3>
            <p className="mt-5 text-forest-muted text-base md:text-lg leading-relaxed max-w-lg">
              {s.body}
            </p>
            <div className="mt-6 flex gap-2">
              {s.tags.map((t) => (
                <span
                  key={t}
                  className="rounded-full border border-forest/30 text-forest text-xs tracking-widest uppercase px-4 py-1.5"
                >
                  {t}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  );
};

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="mx-auto max-w-[1400px] px-6 md:px-12 py-24 md:py-32"
    >
      <Reveal>
        <div className="max-w-3xl mb-8">
          <Label>The Work</Label>
          <h2 className="mt-4 font-serif text-5xl md:text-7xl tracking-tighter text-forest-ink leading-[0.9]">
            Everything a home needs, <span className="italic text-forest">done right.</span>
          </h2>
        </div>
      </Reveal>

      {services.map((s, i) => (
        <Row key={s.n} s={s} i={i} />
      ))}

      <div className="relative border-y border-forest-ink/15 py-12 md:py-16 mt-0">
        <Reveal>
          <span className="pointer-events-none absolute -top-6 md:-top-10 right-0 font-serif text-[22vw] md:text-[12vw] leading-none text-forest-ink/[0.05] select-none">
            06
          </span>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
            <div className="lg:col-span-8">
              <Label>Service — 06</Label>
              <h3 className="mt-4 font-serif text-4xl md:text-5xl tracking-tight text-forest-ink">
                General Property Maintenance
              </h3>
              <p className="mt-4 text-forest-muted text-base md:text-lg max-w-xl">
                The odd jobs, the small fixes, the things on the list you never
                get to. One reliable team for the whole property.
              </p>
            </div>
            <div className="lg:col-span-4 lg:text-right">
              <button
                data-testid="services-quote-btn"
                onClick={() =>
                  document.querySelector("#quote")?.scrollIntoView({ behavior: "smooth" })
                }
                className="rounded-full bg-forest text-bone px-7 py-3 text-sm tracking-wide hover:bg-forest-dark transition-colors duration-300"
              >
                Book a job
              </button>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
